import React from "react";
import SourceConfidenceBadge from "./SourceConfidenceBadge";
import { comparisonSeries, gapText, periodWindow } from "./helpers";

/** Assessment wording mapped to its severity class. */
const ASSESSMENT_CLASS = {
  Strong: "is-strong",
  "Above Average": "is-above",
  Average: "is-average",
  "Needs Attention": "is-attention",
};

/**
 * Benchmarks every KPI against the chosen comparison baseline.
 *
 * @param {object} props - Payload, current period and compare mode, row handler.
 * @returns {JSX.Element|null} The table, or nothing until the section completes.
 *
 * @remarks
 * Rows are keyboard-operable as well as clickable: the drill-down carries the
 * three-year history and the AI's narrative, which is the only place some of
 * that reasoning appears.
 */
const EsgBenchmarkTable = ({ payload, period, compareMode, onSelectKpi, onWhyKpis, onWhyMateriality }) => {
  if (!payload?.kpis?.length) return null;

  const { to } = periodWindow(payload.years, period);
  const comparisonLabel =
    compareMode === "leader" ? "Sector leader" : "Peer average";

  return (
    <section className="aica-card">
      <div className="aica-card-head">
        <div>
          <h2>ESG benchmark</h2>
          <p className="aica-sub">
            {payload.kpis.length} KPIs across emissions, energy, water, waste,
            workforce and governance — select a row for its full history.
          </p>
        </div>
        <div className="aica-why-group">
          <button type="button" className="aica-why" onClick={onWhyKpis}>
            Why these KPIs?
          </button>
          <button type="button" className="aica-why" onClick={onWhyMateriality}>
            Why does this matter?
          </button>
        </div>
      </div>

      <div className="aica-table-wrap">
        <table className="aica-table">
          <thead>
            <tr>
              <th>ESG KPI</th>
              <th>You</th>
              <th>{comparisonLabel}</th>
              <th>Gap</th>
              <th>Assessment</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {payload.kpis.map((kpi) => (
              <tr
                key={kpi.kpiId}
                className="aica-kpi-row"
                role="button"
                tabIndex={0}
                aria-label={`Open ${kpi.name} details`}
                onClick={() => onSelectKpi(kpi)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectKpi(kpi);
                  }
                }}
              >
                <td>
                  <span className="aica-kpi-name">{kpi.name}</span>
                  <span className="aica-kpi-unit">{kpi.unit}</span>
                </td>
                <td className="aica-num">{kpi.series.you[to]}</td>
                <td className="aica-num">
                  {comparisonSeries(kpi.series, compareMode)[to]}
                </td>
                <td className="aica-gap">{gapText(kpi, compareMode, to)}</td>
                <td>
                  <span
                    className={`aica-pill ${
                      ASSESSMENT_CLASS[kpi.assessment] || "is-average"
                    }`}
                  >
                    {kpi.assessment}
                  </span>
                </td>
                <td onClick={(event) => event.stopPropagation()}>
                  <SourceConfidenceBadge provenance={kpi.provenance} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default EsgBenchmarkTable;
